import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { MessageSquare, Send } from "lucide-react";

interface SendTelegramMessageDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function SendTelegramMessageDialog({ open, onOpenChange }: SendTelegramMessageDialogProps) {
  const [message, setMessage] = useState("");
  const [recipientType, setRecipientType] = useState<string>("");
  const [customChatId, setCustomChatId] = useState("");
  const [isSending, setIsSending] = useState(false);
  const { toast } = useToast();

  const sendMessage = async () => {
    if (!message.trim()) {
      toast({
        title: "Սխալ",
        description: "Մուտքագրեք հաղորդագրությունը",
        variant: "destructive",
      });
      return;
    }

    if (!recipientType) {
      toast({
        title: "Սխալ",
        description: "Ընտրեք ստացողին",
        variant: "destructive",
      });
      return;
    }

    if (recipientType === 'custom' && !customChatId.trim()) {
      toast({
        title: "Սխալ",
        description: "Նշեք Chat ID-ն",
        variant: "destructive",
      });
      return;
    }

    setIsSending(true);
    try {
      const response = await supabase.functions.invoke('send-telegram-message', {
        body: {
          message: message.trim(),
          recipientType,
          customChatId: recipientType === 'custom' ? customChatId.trim() : undefined
        }
      });

      if (response.error) throw response.error;

      // Save message to history
      await supabase.from('telegram_messages').insert({
        sent_by: (await supabase.auth.getUser()).data.user?.id,
        message_text: message.trim(),
        recipient_type: recipientType,
        recipient_ids: recipientType === 'custom' ? [customChatId.trim()] : null,
        status: 'sent'
      });

      toast({
        title: "Ուղարկված",
        description: "Հաղորդագրությունը հաջողությամբ ուղարկվեց",
      });

      // Reset form
      setMessage("");
      setRecipientType("");
      setCustomChatId("");
      onOpenChange(false);
    } catch (error) {
      console.error('Error sending telegram message:', error);
      toast({
        title: "Սխալ",
        description: "Չհաջողվեց ուղարկել հաղորդագրությունը",
        variant: "destructive",
      });
    } finally {
      setIsSending(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5" />
            Telegram հաղորդագրություն ուղարկել
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="recipient">Ստացող</Label>
            <Select value={recipientType} onValueChange={setRecipientType}>
              <SelectTrigger>
                <SelectValue placeholder="Ընտրեք ստացողին" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Բոլորը</SelectItem>
                <SelectItem value="students">Ուսանողներ</SelectItem>
                <SelectItem value="lecturers">Դասախոսներ</SelectItem>
                <SelectItem value="admins">Ադմիններ</SelectItem>
                <SelectItem value="custom">Կոնկրետ Chat ID</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {recipientType === 'custom' && (
            <div className="space-y-2">
              <Label htmlFor="customChatId">Chat ID</Label>
              <Input
                id="customChatId"
                placeholder="-1001234567890"
                value={customChatId}
                onChange={(e) => setCustomChatId(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Կոնկրետ chat-ի կամ օգտատիրոջ ID-ն
              </p>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="message">Հաղորդագրություն</Label>
            <Textarea
              id="message"
              placeholder="Մուտքագրեք հաղորդագրությունը..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={6}
            />
          </div>

          <div className="flex justify-end space-x-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Չեղարկել
            </Button>
            <Button 
              onClick={sendMessage} 
              disabled={isSending || !message.trim() || !recipientType}
              className="flex items-center gap-2"
            >
              <Send className="h-4 w-4" />
              {isSending ? "Ուղարկվում է..." : "Ուղարկել"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}